import type { EffectRegion } from "@client/types/vision";
import type React from "react";
import { RegionControls } from "./RegionControls";

interface ImageSizeToolProps {
	magnification: number;
	roiValue: number;
	onChange: (magnification: number) => void;
	onRoiValueChange: (value: number) => void;
	effectRegion: EffectRegion | null;
	regionDrawMode: "none" | "drawing";
	regionPoints: Array<{ x: number; y: number }>;
	onStartDrawing: () => void;
	onUndoPoint: () => void;
	onCancelDrawing: () => void;
	onClearRegion: () => void;
}

export const ImageSizeTool: React.FC<ImageSizeToolProps> = ({
	magnification,
	roiValue,
	onChange,
	onRoiValueChange,
	effectRegion,
	regionDrawMode,
	regionPoints,
	onStartDrawing,
	onUndoPoint,
	onCancelDrawing,
	onClearRegion,
}) => {
	const presets = [0.5, 1, 1.5];
	return (
		<div className="p-4">
			<div className="mb-3 font-semibold">Image Brightness</div>

			<RegionControls
				effectRegion={effectRegion}
				regionDrawMode={regionDrawMode}
				regionPoints={regionPoints}
				onStartDrawing={onStartDrawing}
				onUndoPoint={onUndoPoint}
				onCancelDrawing={onCancelDrawing}
				onClearRegion={onClearRegion}
			/>

			{/* Region slider FIRST when a region is drawn (the user's primary
			    intent after drawing). Base slider second. */}
			{effectRegion && (
				<div className="mb-3">
					<div className="text-lg font-bold tabular-nums mb-1">
						Region brightness {Math.round(roiValue * 100)}%
					</div>
					<input
						type="range"
						min={0.5}
						max={1.5}
						step={0.01}
						value={roiValue}
						onChange={(e) => onRoiValueChange(Number(e.target.value))}
						aria-label="Region brightness"
						className="w-full"
					/>
				</div>
			)}

			<div className="flex gap-2 mb-2">
				{presets.map((p) => (
					<button
						key={p}
						className={`px-2 py-1 rounded ${magnification === p ? "bg-blue-500 text-white" : "bg-gray-200"}`}
						onClick={() => onChange(p)}
					>
						{Math.round(p * 100)}%
					</button>
				))}
			</div>
			{effectRegion && (
				<div className="text-xs text-gray-600 mb-1">
					Base brightness {Math.round(magnification * 100)}%
				</div>
			)}
			<input
				type="range"
				min={0.5}
				max={1.5}
				step={0.01}
				value={magnification}
				onChange={(e) => onChange(Number(e.target.value))}
				aria-label="Base image brightness"
			/>
			<div className="text-xs mt-1 text-gray-500">
				Adjust the overall image brightness.
			</div>
		</div>
	);
};
